// Drop tables (decision 0053: tables per enemy, chest and breakable; spike 002 drops fixed, curated items). A table
// is lines of an item and a count range, each with a chance; or, with `pick: "one"`, one line chosen by weight. The
// game rolls a table on its seeded stream when something breaks or falls. Phase D1 adds rarity tiers and the slot for
// random affixes.
import type { ItemTable } from "./items";
import { rollYields, type Gathered, type YieldDef } from "./gather";

/** One line of a drop table. */
export interface DropLine extends YieldDef {
  /** With `pick: "one"`: this line's weight. Default 1. */
  weight?: number;
}

/** A drop table. */
export interface DropTable {
  id: string;
  /** "all": every line rolls its own chance (default); "one": one line by weight, then its count. */
  pick?: "all" | "one";
  lines: readonly DropLine[];
}

/** Problems with drop tables against the item table (a line naming a missing item, bad numbers); empty when clean. */
export function checkDropTables(tables: readonly DropTable[], items: ItemTable): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const t of tables) {
    if (seen.has(t.id)) out.push(`drop table "${t.id}" is defined twice`);
    seen.add(t.id);
    for (const l of t.lines) {
      if (!items.has(l.item)) out.push(`drop table "${t.id}" names a missing item "${l.item}"`);
      if (!Number.isInteger(l.min) || !Number.isInteger(l.max) || l.min < 0 || l.max < l.min) out.push(`drop table "${t.id}": ${l.item} ${l.min}..${l.max}`);
      if (l.weight !== undefined && !(l.weight > 0)) out.push(`drop table "${t.id}": ${l.item} weight ${l.weight}`);
    }
  }
  return out;
}

/** Roll a table on a seeded stream (`random` in [0, 1)). */
export function rollDrops(t: DropTable, random: () => number): Gathered[] {
  if (t.pick !== "one") return rollYields(t.lines, random);
  const total = t.lines.reduce((n, l) => n + (l.weight ?? 1), 0);
  let r = random() * total;
  for (const l of t.lines) {
    r -= l.weight ?? 1;
    if (r < 0) return rollYields([{ ...l, chance: 1 }], random);
  }
  return [];
}
